import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';

import { typeBlockedReason } from '../utils/widgetTypeAvailability.js';
import CategoryPill from './CategoryPill.jsx';
import PickerOptionGroup from './PickerOptionGroup.jsx';
import WidgetTypeTile from './WidgetTypeTile.jsx';

/** `All` pill: not a category, so it stays out of the registry. */
const ALL_CATEGORIES = 'all';

/** Tiles per row at the widest breakpoint — the arrow-key row stride. */
const GRID_COLUMNS = 4;

/**
 * `1 Widget type` (CT-5196).
 *
 * The catalog comes from `POST /widget/type/list` — the backend owns the
 * vocabulary, and `/widget/create` rejects a `typeCode` that is not in it. This
 * component holds no per-type knowledge beyond the mode gate below; only the
 * icon is resolved locally, because the catalog publishes none.
 *
 * **Every type is on the grid; the ones that cannot be published are dimmed.**
 * The catalog is what tells an author what the product can make, so nothing is
 * hidden. A tile
 * is disabled for one of two reasons, and the tooltip says which:
 *
 *   1. **The data mode.** The backend pairs the mode with the type and refuses
 *      the rest at publish (`WIDGET_PRESENTATION_DATA_MODE_MISMATCH`) — a
 *      Metric row over a record set, a Table over a summarize. Read from the
 *      catalog's own `allowedDataModes`, so this holds no list of its own.
 *   2. **The builder cannot finish its contract** — see `UNBUILDABLE_TYPES`,
 *      which lives beside the code that leaves the gap.
 *   3. **The mode cannot produce a shape the type accepts.** Derived from the
 *      catalog's own columns; `typeBlockedReason` explains it.
 *
 * Everything else stays clickable, including the types whose only obstacle is
 * the data at hand rather than the builder.
 *
 * The catalog arrives as props rather than being fetched here: the Configure
 * tab reads the selected type's `configSchema` for its binding slots and the
 * save reads it again, and three components calling the same hook would be
 * three requests for one list.
 */
function WidgetTypePicker({
  groups: allGroups, isLoading, error, dataMode, datasetShape, selectedType, onSelect,
}) {
  const { t } = useTranslation('dashboards');
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);

  // Why this type cannot be chosen, or `null` when it can — see
  // `typeBlockedReason`, which owns all three reasons so the tooltip and the
  // rule cannot drift apart.
  const blockedReason = useCallback(
    (item) => typeBlockedReason(item, dataMode, datasetShape),
    [dataMode, datasetShape],
  );

  // Changing the Data step's mode can leave the chosen type on a tile that is
  // now disabled; it would still save, then be refused at publish. Only once
  // the catalog has loaded, or a type read back for edit would be cleared
  // before its tile ever existed.
  useEffect(() => {
    if (isLoading || !selectedType || allGroups.length === 0) return;
    const chosen = allGroups
      .flatMap((group) => group.types)
      .find((item) => item.code === selectedType);
    if (chosen && blockedReason(chosen)) onSelect('');
  }, [allGroups, blockedReason, isLoading, onSelect, selectedType]);

  const groups = useMemo(
    () => allGroups.filter(
      (group) => activeCategory === ALL_CATEGORIES || activeCategory === group.id,
    ),
    [activeCategory, allGroups],
  );

  // Labels come from the catalog; only `All` is ours, because it is not a
  // category.
  const pills = useMemo(
    () => [
      { id: ALL_CATEGORIES, label: t('widgetBuilder.typePicker.all') },
      ...allGroups.map((group) => ({ id: group.id, label: group.label })),
    ],
    [allGroups, t],
  );

  const handleCategory = useCallback((id) => setActiveCategory(id), []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {pills.map(({ id, label }) => (
          <CategoryPill
            key={id}
            id={id}
            label={label}
            selected={activeCategory === id}
            onSelect={handleCategory}
          />
        ))}
      </div>

      {error ? (
        <p className="m-0 text-xs text-content-muted">{t('widgetBuilder.typePicker.error')}</p>
      ) : null}

      {!error && isLoading && groups.length === 0 ? (
        <p className="m-0 text-xs text-content-muted">{t('widgetBuilder.typePicker.loading')}</p>
      ) : null}

      {groups.map((group) => (
        <div key={group.id} className="flex min-w-0 flex-col gap-2">
          <div className="flex min-w-0 flex-col">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
              {group.label}
            </p>
            <p className="m-0 text-xs text-content-muted">
              {group.description}
            </p>
          </div>

          <PickerOptionGroup
            ariaLabel={group.label}
            columns={GRID_COLUMNS}
            className="grid grid-cols-3 gap-2 sm:grid-cols-4"
          >
            {group.types.map((entry) => {
              const reason = blockedReason(entry);
              return (
                <WidgetTypeTile
                  key={entry.code}
                  typeId={entry.code}
                  icon={entry.icon}
                  label={entry.label}
                  selected={selectedType === entry.code}
                  disabled={Boolean(reason)}
                  disabledReason={reason ? t(`widgetBuilder.typePicker.blocked.${reason}`) : undefined}
                  onSelect={onSelect}
                />
              );
            })}
          </PickerOptionGroup>
        </div>
      ))}
    </div>
  );
}

WidgetTypePicker.propTypes = {
  /** The catalog, grouped by category — see `useWidgetTypes`. */
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /** `SUMMARY` or `RECORD_SET`, from the Data step's operation. */
  dataMode: PropTypes.oneOf(['SUMMARY', 'RECORD_SET']).isRequired,
  /** The existing dataset's fixed shape, when the source is one. */
  datasetShape: PropTypes.oneOf(['single_value', 'grouped', 'record_set']),
  selectedType: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default WidgetTypePicker;
